/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  createFileRoute,
  useNavigate,
  useRouter,
  useSearch,
} from "@tanstack/react-router";
import type { UseQueryResult } from "@tanstack/react-query";
import { Activity, useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn, humanizedDurationsMap, humanizeDuration, generateTextShadow, USDC_ICON } from "@/lib/utils";
import { ArrowLeft } from "lucide-react";
import { useTournamentDetails } from "@/lib/hooks/useApi";
import { ArenaBar } from "@/components/ArenaBar";
import dayjs from "dayjs";
import { getSyncedNow, updateClockOffset } from "@/lib/clockSync";
import { Countdown } from "@/components/ui/countdown";
import type { Player, Payout } from "@/lib/types/api";
import type { TournamentDetailsResponse } from "@/lib/types/api";
import { FloatingLeftButton } from "@/components/FloatingLeftButton";
import { NavigationLive } from "@/components/NavigationLive";
import { TournamentEnded } from "./-components/TournamentEnded";
import { TournamentOpen } from "./-components/TournamentOpen";
import AnimatedLoading from "@/components/AnimatedLoading";
import AnimatedWaiting from "@/components/AnimatedWaiting";
import ArenaChartECharts from "@/components/ArenaChartECharts";
import { t } from "@/lib/i18n";
import configs from "@/lib/configs";
import { useMusic } from "@/lib/hooks/useMusic";
import { MATCH_TRACK, MENU_TRACK } from "@/lib/configs/music";

type TournamentStatus = "waiting" | "live" | "completed";

type TournamentSearch = {
  fromCreate?: boolean;
};

type TournamentPlayerJoinedEvent = {
  type: "tournament_player_joined";
  tournament_id: string;
  joined_count: number;
  player: Player | null;
  startDateEpoch?: number | null;
  endDateEpoch?: number | null;
};

type TournamentStatusChangedEvent = {
  type: "tournament_status_changed";
  tournament_id: string;
  old_status: string;
  new_status: string;
  bet_amount: number;
  tournament_type: string;
};

function TournamentDetails() {
  const { tournamentId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [liveSquadValues, setLiveSquadValues] = useState<Map<string, number>>(
    new Map(),
  );
  const [data, setData] = useState<TournamentDetailsResponse | null>(null);
  const [isSseErrorOpen, setIsSseErrorOpen] = useState(false);
  const hasSnapshotRef = useRef(false);

  const [tournamentStatus, setTournamentStatus] =
    useState<TournamentStatus | null>(null);
  const router = useRouter();

  const apiResponse = useTournamentDetails(tournamentId, {
    refetchInterval: false,
  });
  const {
    data: initialTournament,
    isPending: isTournamentQueryPending,
    error,
    refetch: refetchTournamentDetails,
  } = apiResponse;

  const tournament = data ?? initialTournament;

  // SSE: real-time updates for this tournament (?kind=tournament)
  useEffect(() => {
    if (!tournamentId) return;
    if (initialTournament?.status === "completed") return;
    let isActive = true;
    const eventSource = new EventSource(
      `${configs.API_BASE_URL}/sse/tournaments/${tournamentId}?kind=tournament`,
    );

    const handleSnapshot = (event: Event) => {
      if (!isActive) return;
      try {
        const messageEvent = event as MessageEvent<string>;
        const payload = JSON.parse(messageEvent.data) as
          | (TournamentDetailsResponse & { serverTime?: number })
          | null;
        if (payload && typeof payload.serverTime === "number") {
          updateClockOffset(payload.serverTime);
        }
        setData(payload ?? null);
        setIsSseErrorOpen(false);
        hasSnapshotRef.current = true;
      } catch (err) {
        console.error("Failed to parse tournament snapshot:", err);
        setIsSseErrorOpen(true);
      }
    };

    const handlePlayerJoined = (event: Event) => {
      if (!isActive) return;
      try {
        const messageEvent = event as MessageEvent<string>;
        const payload = JSON.parse(
          messageEvent.data,
        ) as TournamentPlayerJoinedEvent;
        setData((prev) => {
          if (!prev || prev.tournamentId !== payload.tournament_id) return prev;
          let nextPlayers = prev.players;
          if (payload.player) {
            const exists = prev.players.some(
              (p) => p.squadAddress === payload.player?.squadAddress,
            );
            if (!exists) nextPlayers = [...prev.players, payload.player];
          }
          return {
            ...prev,
            joinedPlayers: nextPlayers.length,
            players: nextPlayers,
          };
        });
      } catch (err) {
        console.error("Failed to parse tournament player joined:", err);
      }
    };

    const handleStatusChanged = (event: Event) => {
      if (!isActive) return;
      try {
        const messageEvent = event as MessageEvent<string>;
        const payload = JSON.parse(
          messageEvent.data,
        ) as TournamentStatusChangedEvent;
        setData((prev) => {
          if (!prev || prev.tournamentId !== payload.tournament_id) return prev;
          return { ...prev, status: payload.new_status };
        });
        if (payload.new_status === "completed") {
          refetchTournamentDetails().then((result) => {
            if (isActive && result.data) setData(result.data);
          });
        }
      } catch (err) {
        console.error("Failed to parse tournament status changed:", err);
      }
    };

    const handleMessage = (event: MessageEvent<string>) => {
      if (!isActive) return;
      try {
        const payload = JSON.parse(event.data) as Record<string, unknown> & {
          type?: string;
          tournament_id?: string;
        };
        if (!payload || typeof payload !== "object") return;
        if ("tournamentId" in payload) {
          handleSnapshot(event as Event);
          return;
        }
        if (payload.type === "tournament_player_joined")
          handlePlayerJoined(event as Event);
        else if (payload.type === "tournament_status_changed")
          handleStatusChanged(event as Event);
      } catch (err) {
        console.error("Failed to parse tournament message:", err);
      }
    };

    eventSource.addEventListener("snapshot", handleSnapshot);
    eventSource.addEventListener("tournament_player_joined", handlePlayerJoined);
    eventSource.addEventListener("tournament_status_changed", handleStatusChanged);
    eventSource.addEventListener("message", handleMessage);
    const handleStreamError = () => {
      if (!isActive) return;
      setIsSseErrorOpen(true);
    };
    eventSource.addEventListener("error", handleStreamError);
    eventSource.onerror = handleStreamError;

    return () => {
      isActive = false;
      eventSource.close();
    };
  }, [tournamentId, initialTournament?.status, refetchTournamentDetails]);

  useEffect(() => {
    if (hasSnapshotRef.current || !initialTournament) return;
    setData((prev) => prev ?? initialTournament);
  }, [initialTournament]);

  const isLive = useMemo(() => tournament?.status === "live", [tournament]);
  const isEnded = useMemo(
    () => tournament?.status === "completed",
    [tournament],
  );
  const isWaiting = useMemo(
    () => tournament?.status === "waiting",
    [tournament],
  );

  const { play } = useMusic();
  useEffect(() => {
    if (isLive) {
      play({ ...MATCH_TRACK, autoPlay: true });

      return () => {
        play({ ...MENU_TRACK, autoPlay: true });
      };
    }
  }, [isLive, play]);

  const isNewTournament = useMemo(() => {
    if (!tournament) return true;
    return tournament.joinedPlayers > 10;
  }, [tournament]);

  useEffect(() => {
    if (!tournament) return;
    setTournamentStatus(tournament.status as TournamentStatus);
  }, [tournament]);

  useEffect(() => {
    if (!tournament) return;
    let targetTimestamp: number | null = null;
    if (tournament.status === "waiting") {
      targetTimestamp = Number(tournament.startDate);
    } else if (tournament.status === "live") {
      targetTimestamp = Number(tournament.endDate);
    } else {
      return;
    }

    const calculateTimeLeft = async () => {
      const now = dayjs(getSyncedNow());
      const target = dayjs.unix(targetTimestamp!);
      const diff = target.diff(now, "second");

      if (diff <= 0) {
        await refetchTournamentDetails();
        return;
      }
    };

    calculateTimeLeft();
    const interval = setInterval(calculateTimeLeft, 3000);
    return () => clearInterval(interval);
  }, [tournament, refetchTournamentDetails]);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return <AnimatedLoading />;
  }

  if (isTournamentQueryPending && !tournament) {
    return <AnimatedLoading />;
  }

  if (error || !tournament) {
    return (
      <div className="bg-gray-50 pt-20">
        <div className="max-w-4xl mx-auto p-4">
          <Button
            onClick={() => router.history.back()}
            className="mb-6 bg-gray-600 hover:bg-gray-700 text-white z-50"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t("tournamentDetails.backToTournaments")}
          </Button>

          <div className="text-center py-12 bg-white rounded-2xl border border-gray-200">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {t("tournamentDetails.tournamentNotFound")}
            </h3>
            <p className="text-gray-600">
              {t("tournamentDetails.tournamentDoesNotExist")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const handlePlayerClick = (player: Player) => {
    setSelectedPlayer(player);
  };

  const handleSquadValuesUpdate = (squadValues: Map<string, number>) => {
    setLiveSquadValues(squadValues);
  };

  const sseErrorOverlay = (
    <Activity mode={isSseErrorOpen ? "visible" : "hidden"}>
      <div className="fixed inset-0 z-1002! flex items-center justify-center bg-indigo-950/90 backdrop-blur-xs px-6 text-left">
        <div className="relative w-88 sm:w-[24rem] flex flex-col items-center gap-6 text-center overflow-x-visible">
          <div className="relative flex flex-col items-center justify-center w-full h-40 overflow-x-visible">
            <img
              src={"/assets/connection-error.png"}
              alt="Info"
              className="h-40 object-contain"
              style={{ imageRendering: "pixelated" }}
            />
          </div>
          <div className="flex flex-col items-center">
            <div className="text-2xl font-bold z-100 uppercase">
              <AnimatedWaiting
                prefix="Reconnecting"
                className="text-white!"
                style={{ textShadow: generateTextShadow(1.5) }}
              />
            </div>
          </div>
        </div>
      </div>
    </Activity>
  );

  return (
    <>
      {sseErrorOverlay}
      <div
      className={cn(
        "flex-1 flex flex-col",
        !isEnded && "bg-gray-50",
        isEnded && "overflow-hidden relative",
      )}
    >
      <div className="flex flex-col flex-1 min-h-0">
        {/* Back Button */}
        {!isWaiting && (
          <FloatingLeftButton
            onClick={() => {
              if (search?.fromCreate) {
                navigate({ to: "/tournament" });
              } else {
                router.history.back();
              }
            }}
            className="absolute left-0 top-4 z-50"
            icon={
              <svg
                className="w-4 h-4"
                viewBox="0 0 9 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3 1V0H2V1H1V2H0V3H1V4H2V5H3V4H6V7H3V8H7V7H8V6H9V3H8V2H7V1H3Z"
                  fill="white"
                />
              </svg>
            }
          />
        )}

        {isWaiting && <TournamentOpen tournamentId={tournamentId} />}

        {isLive && (
          <>
            {/* Tournament Header - Minimal Info */}
            <div
              className="h-full max-h-[60svh] flex-1 flex flex-col gap-3 overflow-hidden"
              style={{
                backgroundImage: "url(https://cdn.navalabs.io/assets/bg-live-tournament_JeXUeg.png)",
                backgroundSize: "cover",
                backgroundPosition: "top",
                backgroundRepeat: "no-repeat",
              }}
            >
              {/* Match Information */}
              <div className="flex flex-col items-center pt-2 gap-1">
                {/* Match Name */}
                <span className="text-white font-black text-2xl tracking-wider text-center">
                  {tournament.tournamentName === "Daily Tournament"
                    ? t("tournamentDetails.tournamentNames.dailyTournament")
                    : tournament.tournamentName}
                </span>

                {/* Countdown Bar */}
                <Countdown
                  className="text-white font-black text-lg tracking-wider text-center"
                  startTimestamp={Number(tournament.endDate)}
                />

                {/* Match Details */}
                <div className="flex flex-col gap-1 w-full max-w-xs uppercase text-shadow-black">
                  <div className="flex justify-between items-center">
                    <span className="text-yellow-400 font-bold text-base">
                      {t("tournamentDetails.playersJoined")}
                    </span>
                    <span className="text-white font-bold text-base">
                      {tournament.joinedPlayers}/{tournament.maximumPlayer}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-yellow-400 font-bold text-base">
                      {t("tournamentDetails.duration")}
                    </span>
                    <span className="text-white font-bold text-base">
                      {humanizeDuration(tournament.duration)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-yellow-400 font-bold text-base">
                      {t("tournamentDetails.entryFee")}
                    </span>
                    <div className="flex items-center gap-1">
                      <img
                        src={USDC_ICON}
                        alt={t("tournamentDetails.altTexts.sol")}
                        className="w-4 h-4"
                        style={{ imageRendering: "pixelated" }}
                      />

                      <span className="text-white font-bold text-base">
                        {tournament.entryUsdc}
                      </span>
                      {/* <span className="text-white font-bold text-base">/</span>
                      <img
                        src="https://cdn.navalabs.io/icons/freepass_j7fqhb.png"
                        alt={t("tournamentDetails.altTexts.freePass")}
                        className="w-4 h-4"
                        style={{ imageRendering: "pixelated" }}
                      />
                      <span className="text-white font-bold text-base">
                        {tournament.entryFreePass}
                      </span> */}
                    </div>
                  </div>
                </div>

                {/* Live Arena - Below Match Information */}
              </div>
              <div className="px-1 flex flex-1 mb-2 overflow-hidden">
                {isNewTournament ? (
                  <ArenaBar
                    players={tournament.players}
                    tournament={tournament}
                    onPlayerClick={handlePlayerClick}
                    onSquadValuesUpdate={handleSquadValuesUpdate}
                  />
                ) : (
                  <ArenaChartECharts
                    players={tournament.players}
                    onSquadValuesUpdate={handleSquadValuesUpdate}
                    matchStartedAt={tournament.endDate ? (Number(tournament.endDate) - tournament.duration) * 1000 : undefined}
                  />
                )}
              </div>
            </div>
          </>
        )}

        {isLive && (
          <NavigationLive
            tournamentDetails={tournament}
            className="h-full max-h-[40svh]"
            onSquadSelect={handlePlayerClick}
            selectedPlayer={selectedPlayer}
            liveSquadValues={liveSquadValues}
          />
        )}

        {isEnded && <TournamentEnded tournament={tournament} />}
        </div>
      </div>
    </>
  );
}

export const Route = createFileRoute("/(game)/tournament/$tournamentId")({
  component: TournamentDetails,
  validateSearch: (search: Record<string, unknown>): TournamentSearch => {
    return {
      fromCreate: search.fromCreate === "true" || search.fromCreate === true,
    };
  },
});
